import { Box, Dialog, DialogContent, DialogTitle, IconButton, MenuItem, TextField, useMediaQuery } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import React, { useEffect, useState } from "react";
import CustomButton from "./Button";
import { useToast } from "./Toast";
import apiService from "../Services/api.service";
import { UI, btnSx } from "./ActionDrawer";

const Btn = ({ variant = "solid", sx, ...props }) => (
  <CustomButton {...props} sx={{ ...btnSx(variant, props.disabled), ...sx }} />
);

const CATEGORIES = [
  { value: "travel", label: "Travel" },
  { value: "fuel", label: "Fuel" },
  { value: "accommodation", label: "Accommodation" },
  { value: "food", label: "Food allowance" },
  { value: "parts", label: "Parts / consumables" },
  { value: "courier", label: "Courier" },
  { value: "other", label: "Other" },
];

const today = () => new Date().toISOString().split("T")[0];

const FOOD_DAILY_LIMIT_RUPEES = 550;

const foodTotalForDay = (day) =>
  day.entries
    .filter((e) => e.category === "food")
    .reduce((sum, e) => sum + (Number(e.amountRupees) || 0), 0);

const blankEntry = () => ({ category: "", amountRupees: "", travelMode: "", distanceKm: "" });
const blankDay = () => ({ date: today(), entries: [blankEntry()] });

// 16px input font stops iOS Safari from auto-zooming on focus; 48px min
// height keeps every tappable control comfortably thumb-sized.
const mobileFieldSx = {
  "& .MuiInputBase-input": { padding: "13px 12px", fontSize: 16 },
  "& .MuiNativeSelect-select": { padding: "13px 12px", fontSize: 16 },
  "& .MuiInputLabel-root": { fontSize: 15 },
};

const toggleBtnSx = (active) => ({
  flex: 1,
  minHeight: "44px",
  border: "1px solid",
  borderColor: active ? UI.p : UI.line,
  borderRadius: "8px",
  background: active ? UI.p : UI.surface,
  color: active ? "#fff" : UI.ink2,
  fontSize: 14,
  fontWeight: 500,
  cursor: "pointer",
});

/* Technicians file this on their phones, so it's one field per line,
   native pickers (bigger tap targets, familiar OS UI) instead of MUI
   menus, and big buttons instead of small text links. Organized by
   date — a stay is a list of days, each day can carry several expense
   categories. Flattened into the backend's flat items[] only at submit.
   No receipt upload for now. */
const FileExpenseClaimModal = ({ open, onClose, onSaved, orderId, clinicName, orderNumber }) => {
  const toast = useToast();
  const token = localStorage.getItem("token");
  const isMobile = useMediaQuery("(max-width:600px)");

  const [days, setDays] = useState([blankDay()]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setDays([blankDay()]);
  }, [open]);

  const updateDay = (dayIndex, patch) => {
    setDays((prev) => prev.map((d, i) => (i === dayIndex ? { ...d, ...patch } : d)));
  };
  const updateEntry = (dayIndex, entryIndex, patch) => {
    setDays((prev) =>
      prev.map((d, i) =>
        i === dayIndex
          ? { ...d, entries: d.entries.map((e, j) => (j === entryIndex ? { ...e, ...patch } : e)) }
          : d,
      ),
    );
  };

  const addDay = () => setDays((prev) => [...prev, blankDay()]);
  const removeDay = (dayIndex) => setDays((prev) => prev.filter((_, i) => i !== dayIndex));

  const addEntry = (dayIndex) =>
    setDays((prev) => prev.map((d, i) => (i === dayIndex ? { ...d, entries: [...d.entries, blankEntry()] } : d)));
  const removeEntry = (dayIndex, entryIndex) =>
    setDays((prev) =>
      prev.map((d, i) => (i === dayIndex ? { ...d, entries: d.entries.filter((_, j) => j !== entryIndex) } : d)),
    );

  const allEntries = days.flatMap((d) => d.entries.map((e) => ({ ...e, date: d.date })));
  const total = allEntries.reduce((sum, e) => sum + (Number(e.amountRupees) || 0), 0);
  const itemCount = allEntries.length;

  const validate = () => {
    for (const [dayIndex, day] of days.entries()) {
      if (!day.date) return `Day ${dayIndex + 1}: enter a date`;
      for (const [entryIndex, entry] of day.entries.entries()) {
        if (!entry.category) return `Day ${dayIndex + 1}, row ${entryIndex + 1}: choose a category`;
        if (!entry.amountRupees || Number(entry.amountRupees) <= 0)
          return `Day ${dayIndex + 1}, row ${entryIndex + 1}: enter a valid amount`;
      }
      const foodTotal = foodTotalForDay(day);
      if (foodTotal > FOOD_DAILY_LIMIT_RUPEES) {
        return `Day ${dayIndex + 1}: food total ₹${foodTotal} is over the ₹${FOOD_DAILY_LIMIT_RUPEES}/day limit`;
      }
    }
    return null;
  };

  const handleSubmit = async () => {
    const error = validate();
    if (error) {
      toast.error(error);
      return;
    }
    setSubmitting(true);
    try {
      await apiService.createExpenseClaim(
        {
          orderId: orderId || undefined,
          submit: true,
          items: allEntries.map((e) => ({
            category: e.category,
            categoryDetail: { mode: e.travelMode, distanceKm: e.distanceKm },
            amountPaise: Math.round(Number(e.amountRupees) * 100),
            date: e.date,
            description: "",
          })),
        },
        token,
      );
      toast.success("Claim submitted");
      onSaved();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to submit claim");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      fullWidth={!isMobile}
      maxWidth="sm"
      PaperProps={{
        sx: {
          bgcolor: UI.surface,
          backgroundImage: "none",
          border: isMobile ? "none" : "1px solid",
          borderColor: UI.line,
          borderRadius: isMobile ? 0 : "10px",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "10px",
          padding: "16px 18px",
          borderBottom: "1px solid",
          borderColor: UI.line,
        }}
      >
        <Box>
          <Box sx={{ fontSize: 18, fontWeight: 600, color: UI.ink }}>New expense claim</Box>
          <Box sx={{ fontSize: 13, color: UI.ink3, marginTop: "2px" }}>
            {orderId ? [clinicName, orderNumber].filter(Boolean).join(" · ") : "Not linked to an order"}
          </Box>
        </Box>
        <IconButton onClick={onClose} size="medium">
          <CloseIcon sx={{ fontSize: 22 }} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ padding: "16px", paddingBottom: "90px" }}>
        <Box sx={{ fontSize: 13, color: UI.ink3, marginBottom: "16px" }}>
          Add each day you were out, and what you spent on that day.
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {days.map((day, dayIndex) => (
            <Box
              key={dayIndex}
              sx={{ border: "1px solid", borderColor: UI.line, borderRadius: "10px", overflow: "hidden" }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "10px",
                  padding: "12px",
                  bgcolor: UI.bg2,
                  borderBottom: "1px solid",
                  borderColor: UI.line,
                }}
              >
                <TextField
                  type="date"
                  label="Date"
                  value={day.date}
                  onChange={(e) => updateDay(dayIndex, { date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  sx={{ bgcolor: UI.surface, flex: 1, ...mobileFieldSx }}
                />
                {days.length > 1 && (
                  <Box
                    component="button"
                    type="button"
                    onClick={() => removeDay(dayIndex)}
                    sx={{
                      minHeight: "40px",
                      padding: "0 10px",
                      border: "1px solid",
                      borderColor: UI.line,
                      borderRadius: "8px",
                      background: UI.surface,
                      fontSize: 13,
                      color: UI.ink3,
                      cursor: "pointer",
                    }}
                  >
                    Remove
                  </Box>
                )}
              </Box>

              <Box sx={{ padding: "12px", display: "flex", flexDirection: "column", gap: "14px" }}>
                {day.entries.map((entry, entryIndex) => (
                  <Box
                    key={entryIndex}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                      padding: day.entries.length > 1 ? "10px" : 0,
                      border: day.entries.length > 1 ? "1px solid" : "none",
                      borderColor: UI.line,
                      borderRadius: "8px",
                    }}
                  >
                    {day.entries.length > 1 && (
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Box sx={{ fontSize: 12, color: UI.ink3, fontWeight: 500 }}>
                          Expense {entryIndex + 1}
                        </Box>
                        <Box
                          component="button"
                          type="button"
                          onClick={() => removeEntry(dayIndex, entryIndex)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            minHeight: "36px",
                            padding: "0 10px",
                            border: "1px solid",
                            borderColor: UI.line,
                            borderRadius: "8px",
                            background: UI.surface,
                            fontSize: 12,
                            color: UI.ink3,
                            cursor: "pointer",
                          }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                          Remove
                        </Box>
                      </Box>
                    )}

                    <TextField
                      select
                      SelectProps={{ native: true }}
                      label="Category"
                      value={entry.category}
                      onChange={(e) => updateEntry(dayIndex, entryIndex, { category: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      sx={mobileFieldSx}
                    >
                      <option value="" disabled></option>
                      {CATEGORIES.map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </TextField>

                    <TextField
                      type="number"
                      inputMode="decimal"
                      label="Amount"
                      placeholder="0"
                      value={entry.amountRupees}
                      onChange={(e) => updateEntry(dayIndex, entryIndex, { amountRupees: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{ startAdornment: <Box sx={{ fontSize: 16, color: UI.ink3, marginRight: "2px" }}>₹</Box> }}
                      fullWidth
                      sx={mobileFieldSx}
                    />

                    {entry.category === "travel" && (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        <Box sx={{ display: "flex", gap: "8px" }}>
                          <Box
                            component="button"
                            type="button"
                            onClick={() => updateEntry(dayIndex, entryIndex, { travelMode: "fare" })}
                            sx={toggleBtnSx(entry.travelMode === "fare")}
                          >
                            Fare
                          </Box>
                          <Box
                            component="button"
                            type="button"
                            onClick={() => updateEntry(dayIndex, entryIndex, { travelMode: "distance" })}
                            sx={toggleBtnSx(entry.travelMode === "distance")}
                          >
                            Distance travelled
                          </Box>
                        </Box>
                        {entry.travelMode === "distance" && (
                          <TextField
                            type="number"
                            inputMode="decimal"
                            label="Distance (km)"
                            value={entry.distanceKm}
                            onChange={(e) => updateEntry(dayIndex, entryIndex, { distanceKm: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            sx={mobileFieldSx}
                          />
                        )}
                      </Box>
                    )}
                  </Box>
                ))}

                <Box
                  component="button"
                  type="button"
                  onClick={() => addEntry(dayIndex)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                    minHeight: "44px",
                    border: "1px dashed",
                    borderColor: UI.p,
                    borderRadius: "8px",
                    background: "none",
                    color: UI.p,
                    fontSize: 14,
                    fontWeight: 500,
                    cursor: "pointer",
                  }}
                >
                  <AddIcon sx={{ fontSize: 17 }} />
                  Add another expense for this day
                </Box>

                {foodTotalForDay(day) > FOOD_DAILY_LIMIT_RUPEES && (
                  <Box sx={{ fontSize: 12.5, color: UI.red }}>
                    Food total ₹{foodTotalForDay(day)} is over the ₹{FOOD_DAILY_LIMIT_RUPEES}/day limit — lower it
                    before submitting.
                  </Box>
                )}
              </Box>
            </Box>
          ))}
        </Box>

        <Box
          component="button"
          type="button"
          onClick={addDay}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "6px",
            width: "100%",
            minHeight: "46px",
            marginTop: "16px",
            border: "1px solid",
            borderColor: UI.line,
            borderRadius: "8px",
            background: UI.bg2,
            color: UI.ink,
            fontSize: 14,
            fontWeight: 500,
            cursor: "pointer",
          }}
        >
          <AddIcon sx={{ fontSize: 18 }} />
          Add another day
        </Box>
      </DialogContent>
      <Box
        sx={{
          position: isMobile ? "fixed" : "static",
          left: 0,
          right: 0,
          bottom: 0,
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "12px 16px",
          borderTop: "1px solid",
          borderColor: UI.line,
          bgcolor: UI.surface,
        }}
      >
        <Box sx={{ fontSize: 14, color: UI.ink2, textAlign: "center" }}>
          {days.length} day{days.length === 1 ? "" : "s"} · {itemCount} item{itemCount === 1 ? "" : "s"} ·{" "}
          <Box component="span" sx={{ fontWeight: 600, color: UI.ink }}>
            ₹{total.toLocaleString("en-IN")}
          </Box>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "center", gap: "10px" }}>
          <Btn
            text="Cancel"
            variant="ghost"
            onClick={onClose}
            sx={{ width: "110px", minHeight: "44px", borderRadius: "10px" }}
          />
          <Btn
            text={submitting ? "Submitting…" : "Submit claim"}
            onClick={handleSubmit}
            disabled={submitting}
            sx={{ width: "160px", minHeight: "44px", fontSize: 15, borderRadius: "10px" }}
          />
        </Box>
      </Box>
    </Dialog>
  );
};

export default FileExpenseClaimModal;
